import { useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { useEntityData } from '../hooks/useSimulaData';
import { useWebSocket } from '../hooks/useWebSocket';

interface NotificationRow {
  id: string;
  title: string;
  message: string;
  type: string;
  createdAt: string;
  isRead: boolean;
}

export function NotificationBell() {
  const [show, setShow] = useState(false);
  const notifications = useEntityData('notifications');
  const { notifications: wsNotifications, unreadCount: wsUnread, markAllRead } = useWebSocket();

  const ordered = useMemo(() => {
    // Merge DB notifications with real-time WebSocket notifications
    const dbNotifs = (notifications.data as unknown as NotificationRow[]).map((n) => ({
      ...n,
      source: 'db' as const,
    }));
    const wsNotifs = wsNotifications.map((n, i) => ({
      id: `ws-${i}`,
      title: n.title,
      message: n.message,
      type: n.type,
      createdAt: new Date().toISOString(),
      isRead: false,
      source: 'ws' as const,
    }));

    // Combine and sort by date (newest first), dedup by title+message
    const all = [...wsNotifs, ...dbNotifs];
    const seen = new Set<string>();
    const deduped = all.filter((n) => {
      const key = `${n.title}:${n.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return deduped.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [notifications.data, wsNotifications]);

  const unreadCount = ordered.filter((n) => !n.isRead).length;

  const markAsRead = async (notif: (typeof ordered)[0]) => {
    if (notif.source === 'db') {
      await notifications.update(notif.id, { isRead: true } as any);
    }
  };

  const handleMarkAllRead = () => {
    markAllRead();
    // Also mark DB notifications as read
    ordered.filter((n) => !n.isRead && n.source === 'db').forEach((n) => {
      notifications.update(n.id, { isRead: true } as any).catch(() => {});
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setShow(!show)}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: 'var(--foreground)',
        }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '8px',
              height: '8px',
              backgroundColor: 'var(--destructive)',
              borderRadius: '50%',
            }}
          />
        )}
      </button>

      {show && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
            onClick={() => setShow(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '350px',
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: 'var(--card)',
              border: '1px solid',
              borderColor: 'var(--border)',
              borderRadius: 'var(--radius-card)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 20,
            }}
          >
            <div className="p-4 border-b" style={{ borderColor: 'var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4>Notificações</h4>
                {unreadCount > 0 && (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginTop: '4px' }}>
                    {unreadCount} não lida{unreadCount > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--primary)',
                  }}
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>
            <div>
              {ordered.length === 0 ? (
                <p className="p-8 text-center" style={{ color: 'var(--muted-foreground)' }}>
                  Nenhuma notificação
                </p>
              ) : (
                ordered.map((notif) => (
                  <div
                    key={notif.id}
                    className="p-4 border-b cursor-pointer"
                    style={{
                      borderColor: 'var(--border)',
                      backgroundColor: notif.isRead ? 'transparent' : 'var(--muted)',
                    }}
                    onClick={() => markAsRead(notif)}
                  >
                    <h4
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        marginBottom: '4px',
                      }}
                    >
                      {notif.title}
                    </h4>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginBottom: '4px' }}>
                      {notif.message}
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', opacity: 0.7 }}>
                      {new Date(notif.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

